/** Simple this-month interest vs principal from balance × rate/12 vs payment. */
export function estimatePaymentSplit(
  balance: number,
  interestRatePct: number,
  monthlyPayment: number,
): { monthlyInterest: number; monthlyPrincipal: number } {
  const safeBalance = Math.max(0, balance);
  const safeRate = Math.max(0, interestRatePct);
  const safePayment = Math.max(0, monthlyPayment);
  const monthlyInterest = Math.round(((safeBalance * safeRate) / 100 / 12) * 100) / 100;
  const monthlyPrincipal = Math.max(0, Math.round((safePayment - monthlyInterest) * 100) / 100);
  return { monthlyInterest, monthlyPrincipal };
}

export type ExtraPayment = {
  /** 1-based payment month index. */
  month: number;
  amount: number;
};

export type AmortizationRow = {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  extra: number;
  balance: number;
};

export type AmortizationSchedule = {
  rows: AmortizationRow[];
  monthlyPayment: number;
  totalInterest: number;
  totalPaid: number;
  monthsToPayoff: number;
};

export type LoanBalancePoint = {
  year: number;
  balance: number;
};

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function monthlyRate(interestRatePct: number): number {
  return Math.max(0, interestRatePct) / 100 / 12;
}

/** Standard fixed-rate monthly principal & interest payment. */
export function calculateMonthlyPayment(
  principal: number,
  interestRatePct: number,
  termMonths: number,
): number {
  const p = Math.max(0, principal);
  const n = Math.max(0, Math.round(termMonths));
  if (p <= 0 || n <= 0) return 0;

  const r = monthlyRate(interestRatePct);
  if (r === 0) return roundMoney(p / n);

  const factor = Math.pow(1 + r, n);
  return roundMoney((p * r * factor) / (factor - 1));
}

/**
 * Remaining term (months) given balance, rate, and fixed payment.
 * Returns null when payment cannot amortize the loan.
 */
export function estimateRemainingTermMonths(
  principal: number,
  interestRatePct: number,
  monthlyPayment: number,
): number | null {
  const p = Math.max(0, principal);
  const m = Math.max(0, monthlyPayment);
  if (p <= 0 || m <= 0) return null;

  const r = monthlyRate(interestRatePct);
  if (r === 0) return Math.ceil(p / m);

  const interestOnly = p * r;
  if (m <= interestOnly) return null;

  const n = Math.log(m / (m - r * p)) / Math.log(1 + r);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.max(1, Math.ceil(n));
}

function extrasByMonth(extras: ExtraPayment[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const extra of extras) {
    if (extra.month < 1 || extra.amount <= 0) continue;
    map.set(extra.month, roundMoney((map.get(extra.month) ?? 0) + extra.amount));
  }
  return map;
}

export function buildAmortizationSchedule(params: {
  principal: number;
  interestRatePct: number;
  termMonths: number;
  /** Override scheduled P&I; defaults to calculated payment for term. */
  monthlyPayment?: number;
  extras?: ExtraPayment[];
  /** Extra principal applied every month on top of one-time extras. */
  recurringMonthlyExtra?: number;
}): AmortizationSchedule {
  const principal = Math.max(0, params.principal);
  const termMonths = Math.max(0, Math.round(params.termMonths));
  const payment =
    params.monthlyPayment != null && params.monthlyPayment > 0
      ? roundMoney(params.monthlyPayment)
      : calculateMonthlyPayment(principal, params.interestRatePct, termMonths);
  const recurring = Math.max(0, roundMoney(params.recurringMonthlyExtra ?? 0));

  if (principal <= 0 || termMonths <= 0 || payment <= 0) {
    return {
      rows: [],
      monthlyPayment: payment,
      totalInterest: 0,
      totalPaid: 0,
      monthsToPayoff: 0,
    };
  }

  const r = monthlyRate(params.interestRatePct);
  const extraMap = extrasByMonth(params.extras ?? []);
  const rows: AmortizationRow[] = [];
  let balance = principal;
  let totalInterest = 0;
  let totalPaid = 0;
  const hardCap = termMonths + 120;

  for (let month = 1; month <= hardCap && balance > 0.01; month += 1) {
    const interest = roundMoney(balance * r);
    let principalPaid = roundMoney(Math.min(balance, Math.max(0, payment - interest)));
    let appliedPayment = roundMoney(principalPaid + interest);

    // Final month: pay only what's left (+ interest).
    if (principalPaid >= balance - 0.005 || month === hardCap) {
      principalPaid = balance;
      appliedPayment = roundMoney(balance + interest);
    }

    const extraRaw = roundMoney((extraMap.get(month) ?? 0) + recurring);
    const extra = roundMoney(Math.min(extraRaw, Math.max(0, balance - principalPaid)));
    const totalPrincipal = roundMoney(principalPaid + extra);
    balance = roundMoney(Math.max(0, balance - totalPrincipal));

    totalInterest = roundMoney(totalInterest + interest);
    totalPaid = roundMoney(totalPaid + appliedPayment + extra);

    rows.push({
      month,
      payment: appliedPayment,
      principal: principalPaid,
      interest,
      extra,
      balance,
    });

    if (balance <= 0.01) {
      balance = 0;
      if (rows.length > 0) {
        rows[rows.length - 1] = { ...rows[rows.length - 1], balance: 0 };
      }
      break;
    }
  }

  return {
    rows,
    monthlyPayment: payment,
    totalInterest,
    totalPaid,
    monthsToPayoff: rows.length,
  };
}

/** Yearly balance series for DualLineChart (year = years from start). */
export function scheduleToYearlyBalance(rows: AmortizationRow[]): LoanBalancePoint[] {
  if (rows.length === 0) return [{ year: 0, balance: 0 }];

  const points: LoanBalancePoint[] = [{ year: 0, balance: rows[0].balance + rows[0].principal + rows[0].extra }];
  for (const row of rows) {
    if (row.month % 12 === 0 || row.balance === 0) {
      const year = Math.ceil(row.month / 12);
      const last = points[points.length - 1];
      if (last.year === year) {
        last.balance = row.balance;
      } else {
        points.push({ year, balance: row.balance });
      }
    }
  }

  const final = rows[rows.length - 1];
  const finalYear = Math.ceil(final.month / 12);
  if (points[points.length - 1]?.year !== finalYear || points[points.length - 1]?.balance !== final.balance) {
    points.push({ year: finalYear, balance: final.balance });
  }

  return points;
}

export function loanDurationParts(months: number): { years: number; months: number } {
  const safe = Math.max(0, Math.round(months));
  return {
    years: Math.floor(safe / 12),
    months: safe % 12,
  };
}

export function formatLoanDuration(months: number): string {
  if (months <= 0) return '—';
  const { years, months: rem } = loanDurationParts(months);
  if (years === 0) return `${rem} mo`;
  if (rem === 0) return `${years} yr`;
  return `${years} yr ${rem} mo`;
}

const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isValidMonthKey(value: unknown): value is string {
  return typeof value === 'string' && MONTH_KEY_RE.test(value);
}

export function parseMonthKey(key: string): { year: number; month: number } | null {
  if (!isValidMonthKey(key)) return null;
  const [year, month] = key.split('-').map(Number);
  return { year, month };
}

export function formatMonthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** Local calendar month `YYYY-MM` — not UTC, so "where you are" matches the user's clock. */
export function currentCalendarMonthKey(now = new Date()): string {
  return formatMonthKey(now.getFullYear(), now.getMonth() + 1);
}

export function addMonthsToMonthKey(startMonth: string, deltaMonths: number): string | null {
  const parsed = parseMonthKey(startMonth);
  if (!parsed) return null;
  const date = new Date(parsed.year, parsed.month - 1 + deltaMonths, 1);
  return formatMonthKey(date.getFullYear(), date.getMonth() + 1);
}

export function formatMonthKeyLabel(monthKey: string): string {
  const parsed = parseMonthKey(monthKey);
  if (!parsed) return monthKey;
  return new Date(parsed.year, parsed.month - 1, 1).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
}

/** 1-based payment index for `asOfMonth` relative to start. 0 if as-of is before start. */
export function paymentMonthIndex(startMonth: string, asOfMonth: string): number {
  const start = parseMonthKey(startMonth);
  const asOf = parseMonthKey(asOfMonth);
  if (!start || !asOf) return 0;
  const delta = (asOf.year - start.year) * 12 + (asOf.month - start.month);
  return delta < 0 ? 0 : delta + 1;
}

export function monthKeyForPayment(
  startMonth: string | null | undefined,
  month: number,
): string | null {
  if (!startMonth || month < 1) return null;
  return addMonthsToMonthKey(startMonth, month - 1);
}

export function relativePaymentPeriod(month: number): string {
  const safe = Math.max(1, Math.round(month));
  const year = Math.ceil(safe / 12);
  const mo = ((safe - 1) % 12) + 1;
  return `Y${year} · M${mo}`;
}

/** Calendar month when start is set, otherwise relative "Y1 · M3". */
export function formatPaymentPeriod(
  month: number,
  startMonth?: string | null,
): string {
  const calendar = monthKeyForPayment(startMonth, month);
  if (calendar) return formatMonthKeyLabel(calendar);
  return relativePaymentPeriod(month);
}

export function calendarYearForOffset(
  startMonth: string | null | undefined,
  yearOffset: number,
): number | null {
  const parsed = startMonth ? parseMonthKey(startMonth) : null;
  if (!parsed) return null;
  return parsed.year + Math.max(0, Math.round(yearOffset));
}

export function formatPayoffMonth(
  startMonth: string | null | undefined,
  monthsToPayoff: number,
): string | null {
  if (!startMonth || monthsToPayoff <= 0) return null;
  const key = monthKeyForPayment(startMonth, monthsToPayoff);
  return key ? formatMonthKeyLabel(key) : null;
}

export type LoanPaidToDate = {
  status: 'unstarted' | 'in_progress' | 'paid_off';
  /** 1-based payment month for as-of, or 0 if the loan has not started. */
  currentMonth: number;
  currentMonthKey: string | null;
  firstPaymentLabel: string;
  paymentsMade: number;
  monthsRemaining: number;
  principalPaid: number;
  interestPaid: number;
  extraPaid: number;
  totalPaid: number;
  remainingBalance: number;
  originalPrincipal: number;
  progressPct: number;
};

/**
 * Schedule-based progress from start month through the as-of calendar month.
 * Returns null when no start month is selected — never infers "now" as the start.
 */
export function summarizePaidToDate(params: {
  rows: AmortizationRow[];
  principal: number;
  startMonth: string | null | undefined;
  asOfMonth?: string;
}): LoanPaidToDate | null {
  const startMonth = params.startMonth;
  if (!isValidMonthKey(startMonth)) return null;

  const asOfMonth = isValidMonthKey(params.asOfMonth)
    ? params.asOfMonth
    : currentCalendarMonthKey();
  const firstPaymentLabel = formatMonthKeyLabel(startMonth);
  const principal = Math.max(0, params.principal);
  const currentMonth = paymentMonthIndex(startMonth, asOfMonth);
  const currentMonthKey =
    currentMonth > 0 ? monthKeyForPayment(startMonth, currentMonth) : startMonth;

  if (currentMonth <= 0) {
    return {
      status: 'unstarted',
      currentMonth: 0,
      currentMonthKey: startMonth,
      firstPaymentLabel,
      paymentsMade: 0,
      monthsRemaining: params.rows.length,
      principalPaid: 0,
      interestPaid: 0,
      extraPaid: 0,
      totalPaid: 0,
      remainingBalance: principal,
      originalPrincipal: principal,
      progressPct: 0,
    };
  }

  let principalPaid = 0;
  let interestPaid = 0;
  let extraPaid = 0;
  let remainingBalance = principal;
  let paymentsMade = 0;

  for (const row of params.rows) {
    if (row.month > currentMonth) break;
    principalPaid += row.principal;
    interestPaid += row.interest;
    extraPaid += row.extra;
    remainingBalance = row.balance;
    paymentsMade = row.month;
  }

  const paidOff =
    params.rows.length > 0 && currentMonth >= params.rows.length && remainingBalance <= 0.01;

  return {
    status: paidOff ? 'paid_off' : 'in_progress',
    currentMonth,
    currentMonthKey,
    firstPaymentLabel,
    paymentsMade,
    monthsRemaining: Math.max(0, params.rows.length - paymentsMade),
    principalPaid: roundMoney(principalPaid),
    interestPaid: roundMoney(interestPaid),
    extraPaid: roundMoney(extraPaid),
    totalPaid: roundMoney(principalPaid + interestPaid + extraPaid),
    remainingBalance: roundMoney(Math.max(0, remainingBalance)),
    originalPrincipal: principal,
    progressPct:
      principal > 0
        ? Math.min(100, Math.round((principalPaid / principal) * 100))
        : 0,
  };
}

/** Cumulative interest paid through the end of `year` (years from start). */
export function interestPaidThroughYear(
  rows: AmortizationRow[],
  year: number,
): number {
  const throughMonth = Math.max(0, Math.round(year) * 12);
  let total = 0;
  for (const row of rows) {
    if (row.month > throughMonth) break;
    total += row.interest;
  }
  return Math.round(total * 100) / 100;
}

export function balanceAtYear(
  series: LoanBalancePoint[],
  year: number,
): number {
  const exact = series.find((p) => p.year === year);
  if (exact) return exact.balance;
  let last = series[0]?.balance ?? 0;
  for (const p of series) {
    if (p.year > year) break;
    last = p.balance;
  }
  return last;
}

export type LoanCostSummary = {
  principal: number;
  totalInterest: number;
  totalCost: number;
  interestSharePct: number;
  principalSharePct: number;
};

export function loanCostSummary(
  principal: number,
  totalInterest: number,
): LoanCostSummary {
  const p = Math.max(0, principal);
  const interest = Math.max(0, totalInterest);
  const totalCost = Math.round((p + interest) * 100) / 100;
  const interestSharePct = totalCost > 0 ? Math.round((interest / totalCost) * 100) : 0;
  return {
    principal: p,
    totalInterest: interest,
    totalCost,
    interestSharePct,
    principalSharePct: Math.max(0, 100 - interestSharePct),
  };
}
